/**
 * Centralized Service Factory for all services
 * STRICT IoC - services injected based on environment/config
 * This follows Dependency Inversion principle
 */

import { AppointmentFilesService } from "./AppointmentFiles/AppointmentFilesService";
import { IAppointmentFilesService } from "./AppointmentFiles/IAppointmentFilesService";
import { AppointmentsService } from "./appointments/AppointmentsService";
import { IAppointmentsService } from "./appointments/IAppointmentsService";
import { AttendeeFilesService } from "./attendeeFiles/AttendeeFilesService";
import { IAttendeeFilesService } from "./attendeeFiles/IAttendeeFilesService";
import { AttendeeHistoryService } from "./attendeeHistory/AttendeeHistoryService";
import { IAttendeeHistoryService } from "./attendeeHistory/IAttendeeHistoryService";
import { AttendeesService } from "./attendees/AttendeesService";
import { IAttendeesService } from "./attendees/IAttendeesService";
import { ClientFilesService } from "./clientFiles/ClientFilesService";
import { IClientFilesService } from "./clientFiles/IClientFilesService";
import { ClientObservationsService } from "./clientObservations/ClientObservationsService";
import { IClientObservationsService } from "./clientObservations/IClientObservationsService";
import { ClientsService } from "./clients/ClientsService";
import { IClientsService } from "./clients/IClientsService";
import { CommunicationService } from "./communication/CommunicationService";
import { ICommunicationService } from "./communication/ICommunicationService";
import { CommunicationManagementService } from "./communicationManagement/CommunicationManagementService";
import { ICommunicationManagementService } from "./communicationManagement/ICommunicationManagementService";
import { EmployeesService } from "./employees/EmployeesService";
import { IEmployeesService } from "./employees/IEmployeesService";
import { IEmployeeSlotsService } from "./employeeSlots/IEmployeeSlotsService";
import { FilesService } from "./files/FilesService";
import { IFilesService } from "./files/IFilesService";
import { BannersService } from "./content/BannersService";
import { ContentPagesService } from "./content/ContentPagesService";
import { NewsService } from "./content/NewsService";
import { IBannersService } from "./content/IBannersService";
import { IContentPagesService } from "./content/IContentPagesService";
import { INewsService } from "./content/INewsService";
import { CommunicationsCenterService } from "./communicationsCenter/CommunicationsCenterService";
import { ICommunicationsCenterService } from "./communicationsCenter/ICommunicationsCenterService";
import { HealthboxService } from "./healthbox/HealthboxService";
import { IHealthboxService } from "./healthbox/IHealthboxService";

import { EmployeeSlotsService } from "./employeeSlots/EmployeeSlotsService";
import { IRefCommunicationTemplatesService } from "./refCommunicationTemplates/IRefCommunicationTemplatesService";
import { RefCommunicationTemplatesService } from "./refCommunicationTemplates/RefCommunicationTemplatesService";
import { IRefOptionsService } from "./refOptions/IRefOptionsService";
import { RefOptionsService } from "./refOptions/RefOptionsService";
import { RefOptionsServiceMock } from "./refOptions/RefOptionsServiceMock";
import { IRefServicesService } from "./refServices/IRefServicesService";
import { RefServicesService } from "./refServices/RefServicesService";
import { IMetricsService } from "./metrics/IMetricsService";
import { MetricsService } from "./metrics/MetricsService";
import { IMetricsReportsService } from "./metricsReports/IMetricsReportsService";
import { EmployeeServicesService } from "./employeeServices/EmployeeServicesService";
import { IEmployeeServicesService } from "./employeeServices/IEmployeeServicesService";
import { MetricsReportsService } from "./metricsReports/MetricsReportsService";
import { IOnlineOrdersService } from "./onlineOrders/IOnlineOrdersService";
import { OnlineOrdersService } from "./onlineOrders/OnlineOrdersService";
import { IStoreNotificationsService } from "./storeNotifications/IStoreNotificationsService";
import { StoreNotificationsService } from "./storeNotifications/StoreNotificationsService";
import { IRefStoresService } from "./refStores/IRefStoresService";
import { RefStoresService } from "./refStores/RefStoresService";
import { IRuleProfileService } from "./RuleProfile/IRuleProfileService";
import { RuleProfileService } from "./RuleProfile/RuleProfileService";
import { IRuleServiceDurationService } from "./ruleServiceDuration/IRuleServiceDurationService";
import { RuleServiceDurationService } from "./ruleServiceDuration/RuleServiceDurationService";
import { ISaleService } from "./sale/ISaleService";
import { SaleService } from "./sale/SaleService";
import { SaleItemsService } from "./saleItems";
import { ISaleItemsService } from "./saleItems/ISaleItemsService";
import { IStockService } from "./stock/IStockService";
import { StockService } from "./stock/StockService";

export type ServiceType = "mock" | "real";

/**
 * Centralized service factory using environment-based injection
 */
export class ServiceFactory {
  private static appointmentsServiceInstance: IAppointmentsService | null =
    null;
  private static attendeesServiceInstance: IAttendeesService | null = null;
  private static clientsServiceInstance: IClientsService | null = null;
  private static clientObservationsServiceInstance: IClientObservationsService | null =
    null;
  private static clientFilesServiceInstance: IClientFilesService | null = null;
  private static employeesServiceInstance: IEmployeesService | null = null;
  private static attendeeHistoryServiceInstance: IAttendeeHistoryService | null =
    null;
  private static attendeeFilesServiceInstance: IAttendeeFilesService | null =
    null;
  private static saleItemsServiceInstance: ISaleItemsService | null = null;
  private static refStoresServiceInstance: IRefStoresService | null = null;
  private static refOptionsServiceInstance: IRefOptionsService | null = null;
  private static refServicesServiceInstance: IRefServicesService | null = null;
  private static ruleProfileServiceInstance: IRuleProfileService | null = null;
  private static refCommunicationTemplatesServiceInstance: IRefCommunicationTemplatesService | null =
    null;
  private static filesServiceInstance: IFilesService | null = null;
  private static appointmentFilesServiceInstance: IAppointmentFilesService | null =
    null;
  private static communicationServiceInstance: ICommunicationService | null =
    null;
  private static communicationManagementServiceInstance: ICommunicationManagementService | null =
    null;
  private static employeeSlotsServiceInstance: IEmployeeSlotsService | null =
    null;
  private static ruleServiceDurationServiceInstance: IRuleServiceDurationService | null =
    null;
  private static stockServiceInstance: IStockService | null = null;
  private static saleServiceInstance: ISaleService | null = null;
  private static healthboxServiceInstance: IHealthboxService | null = null;
  private static communicationsCenterServiceInstance: ICommunicationsCenterService | null =
    null;
  private static bannersServiceInstance: IBannersService | null = null;
  private static contentPagesServiceInstance: IContentPagesService | null =
    null;
  private static newsServiceInstance: INewsService | null = null;
  private static metricsServiceInstance: IMetricsService | null = null;
  private static metricsReportsServiceInstance: IMetricsReportsService | null =
    null;
  private static employeeServicesServiceInstance: IEmployeeServicesService | null =
    null;
  private static storeNotificationsServiceInstance: IStoreNotificationsService | null =
    null;
  private static onlineOrdersServiceInstance: IOnlineOrdersService | null =
    null;

  static getAppointmentsService(): IAppointmentsService {
    if (!this.appointmentsServiceInstance) {
      this.appointmentsServiceInstance = new AppointmentsService();
    }

    return this.appointmentsServiceInstance;
  }

  static setAppointmentsService(service: IAppointmentsService): void {
    this.appointmentsServiceInstance = service;
  }

  static getSaleItemsService(): ISaleItemsService {
    if (!this.saleItemsServiceInstance) {
      this.saleItemsServiceInstance = new SaleItemsService();
    }

    return this.saleItemsServiceInstance;
  }

  static setSaleItemsService(service: ISaleItemsService): void {
    this.saleItemsServiceInstance = service;
  }

  // RefStores Service Methods
  static getRefStoresService(): IRefStoresService {
    if (!this.refStoresServiceInstance) {
      this.refStoresServiceInstance = new RefStoresService();
    }

    return this.refStoresServiceInstance;
  }

  static setRefStoresService(service: IRefStoresService): void {
    this.refStoresServiceInstance = service;
  }

  // Clients Service Methods
  static getClientsService(): IClientsService {
    if (!this.clientsServiceInstance) {
      this.clientsServiceInstance = new ClientsService();
    }

    return this.clientsServiceInstance;
  }

  static setClientsService(service: IClientsService): void {
    this.clientsServiceInstance = service;
  }

  static getClientObservationsService(): IClientObservationsService {
    if (!this.clientObservationsServiceInstance) {
      this.clientObservationsServiceInstance = new ClientObservationsService();
    }

    return this.clientObservationsServiceInstance;
  }

  static setClientObservationsService(
    service: IClientObservationsService,
  ): void {
    this.clientObservationsServiceInstance = service;
  }

  static getClientFilesService(): IClientFilesService {
    if (!this.clientFilesServiceInstance) {
      this.clientFilesServiceInstance = new ClientFilesService();
    }

    return this.clientFilesServiceInstance;
  }

  static setClientFilesService(service: IClientFilesService): void {
    this.clientFilesServiceInstance = service;
  }

  // Attendees Service Methods
  static getAttendeesService(): IAttendeesService {
    if (!this.attendeesServiceInstance) {
      this.attendeesServiceInstance = new AttendeesService();
    }

    return this.attendeesServiceInstance;
  }

  static setAttendeesService(service: IAttendeesService): void {
    this.attendeesServiceInstance = service;
  }

  // Employees Service Methods
  static getEmployeesService(): IEmployeesService {
    if (!this.employeesServiceInstance) {
      this.employeesServiceInstance = new EmployeesService();
    }

    return this.employeesServiceInstance;
  }

  static setEmployeesService(service: IEmployeesService): void {
    this.employeesServiceInstance = service;
  }

  // AttendeeHistory Service Methods
  static getAttendeeHistoryService(): IAttendeeHistoryService {
    if (!this.attendeeHistoryServiceInstance) {
      this.attendeeHistoryServiceInstance = new AttendeeHistoryService();
    }

    return this.attendeeHistoryServiceInstance;
  }

  static setAttendeeHistoryService(service: IAttendeeHistoryService): void {
    this.attendeeHistoryServiceInstance = service;
  }

  // AttendeeFiles Service Methods
  static getAttendeeFilesService(): IAttendeeFilesService {
    if (!this.attendeeFilesServiceInstance) {
      this.attendeeFilesServiceInstance = new AttendeeFilesService();
    }

    return this.attendeeFilesServiceInstance;
  }

  static setAttendeeFilesService(service: IAttendeeFilesService): void {
    this.attendeeFilesServiceInstance = service;
  }

  // RefOptions Service Methods
  static getRefOptionsService(type: ServiceType = "real"): IRefOptionsService {
    if (!this.refOptionsServiceInstance) {
      const useMockAPI = type === "mock";

      this.refOptionsServiceInstance = useMockAPI
        ? new RefOptionsServiceMock()
        : new RefOptionsService();
    }

    return this.refOptionsServiceInstance;
  }

  // RefServices Service Methods
  static getRefServicesService(): IRefServicesService {
    if (!this.refServicesServiceInstance) {
      this.refServicesServiceInstance = new RefServicesService();
    }

    return this.refServicesServiceInstance;
  }

  static setRefServicesService(service: IRefServicesService): void {
    this.refServicesServiceInstance = service;
  }

  static getRuleProfileService(): IRuleProfileService {
    if (!this.ruleProfileServiceInstance) {
      this.ruleProfileServiceInstance = new RuleProfileService();
    }

    return this.ruleProfileServiceInstance;
  }

  // RefCommunicationTemplates Service Methods
  static getRefCommunicationTemplatesService(): IRefCommunicationTemplatesService {
    if (!this.refCommunicationTemplatesServiceInstance) {
      this.refCommunicationTemplatesServiceInstance =
        new RefCommunicationTemplatesService();
    }

    return this.refCommunicationTemplatesServiceInstance;
  }

  // Files Service Methods
  static getFilesService(): IFilesService {
    if (!this.filesServiceInstance) {
      this.filesServiceInstance = new FilesService();
    }

    return this.filesServiceInstance;
  }

  // AppointmentFiles Service Methods
  static getAppointmentFilesService(): IAppointmentFilesService {
    if (!this.appointmentFilesServiceInstance) {
      this.appointmentFilesServiceInstance = new AppointmentFilesService();
    }

    return this.appointmentFilesServiceInstance;
  }

  // Communication Service Methods
  static getCommunicationService(): ICommunicationService {
    if (!this.communicationServiceInstance) {
      this.communicationServiceInstance = new CommunicationService();
    }

    return this.communicationServiceInstance;
  }

  // Communication Management Service Methods
  static getCommunicationManagementService(): ICommunicationManagementService {
    if (!this.communicationManagementServiceInstance) {
      this.communicationManagementServiceInstance =
        new CommunicationManagementService();
    }

    return this.communicationManagementServiceInstance;
  }

  static setCommunicationManagementService(
    service: ICommunicationManagementService,
  ): void {
    this.communicationManagementServiceInstance = service;
  }

  static setRefOptionsService(service: IRefOptionsService): void {
    this.refOptionsServiceInstance = service;
  }

  static getEmployeeSlotsService(): IEmployeeSlotsService {
    if (!this.employeeSlotsServiceInstance) {
      this.employeeSlotsServiceInstance = new EmployeeSlotsService();
    }

    return this.employeeSlotsServiceInstance;
  }

  static setEmployeeSlotsService(service: IEmployeeSlotsService): void {
    this.employeeSlotsServiceInstance = service;
  }

  static getRuleServiceDurationService(): IRuleServiceDurationService {
    if (!this.ruleServiceDurationServiceInstance) {
      this.ruleServiceDurationServiceInstance =
        new RuleServiceDurationService();
    }

    return this.ruleServiceDurationServiceInstance;
  }

  static setRuleServiceDurationService(
    service: IRuleServiceDurationService,
  ): void {
    this.ruleServiceDurationServiceInstance = service;
  }

  static getStockService(): IStockService {
    if (!this.stockServiceInstance) {
      this.stockServiceInstance = new StockService();
    }

    return this.stockServiceInstance;
  }

  static setStockService(service: IStockService): void {
    this.stockServiceInstance = service;
  }

  // Sale Service Methods
  static getSaleService(): ISaleService {
    if (!this.saleServiceInstance) {
      this.saleServiceInstance = new SaleService();
    }

    return this.saleServiceInstance;
  }

  static setSaleService(service: ISaleService): void {
    this.saleServiceInstance = service;
  }

  // Healthbox Service Methods
  static getHealthboxService(): IHealthboxService {
    if (!this.healthboxServiceInstance) {
      this.healthboxServiceInstance = new HealthboxService();
    }

    return this.healthboxServiceInstance;
  }

  static setHealthboxService(service: IHealthboxService): void {
    this.healthboxServiceInstance = service;
  }

  /*
   * Communications Center Service Methods
   *
   * Built from the other domain services rather than opening its own client, so
   * the factory hands them in. That also keeps the dependency one-way: the
   * service importing their barrels would form a cycle back through here.
   */
  static getCommunicationsCenterService(): ICommunicationsCenterService {
    if (!this.communicationsCenterServiceInstance) {
      this.communicationsCenterServiceInstance =
        new CommunicationsCenterService({
          communicationService: this.getCommunicationService(),
          communicationManagementService:
            this.getCommunicationManagementService(),
          refCommunicationTemplatesService:
            this.getRefCommunicationTemplatesService(),
          refServicesService: this.getRefServicesService(),
          refOptionsService: this.getRefOptionsService(),
          refStoresService: this.getRefStoresService(),
        });
    }

    return this.communicationsCenterServiceInstance;
  }

  static setCommunicationsCenterService(
    service: ICommunicationsCenterService,
  ): void {
    this.communicationsCenterServiceInstance = service;
  }

  /*
   * Managed Content Service Methods
   *
   * Banners, content pages and news are three separate resources on the API,
   * so they get three services rather than one content facade: they share only
   * the `/api/admin` base and their paging query.
   */
  static getBannersService(): IBannersService {
    if (!this.bannersServiceInstance) {
      this.bannersServiceInstance = new BannersService();
    }

    return this.bannersServiceInstance;
  }

  static setBannersService(service: IBannersService): void {
    this.bannersServiceInstance = service;
  }

  static getContentPagesService(): IContentPagesService {
    if (!this.contentPagesServiceInstance) {
      this.contentPagesServiceInstance = new ContentPagesService();
    }

    return this.contentPagesServiceInstance;
  }

  static setContentPagesService(service: IContentPagesService): void {
    this.contentPagesServiceInstance = service;
  }

  static getNewsService(): INewsService {
    if (!this.newsServiceInstance) {
      this.newsServiceInstance = new NewsService();
    }

    return this.newsServiceInstance;
  }

  static setNewsService(service: INewsService): void {
    this.newsServiceInstance = service;
  }

  // Metrics Service Methods
  static getMetricsService(): IMetricsService {
    if (!this.metricsServiceInstance) {
      this.metricsServiceInstance = new MetricsService();
    }

    return this.metricsServiceInstance;
  }

  static setMetricsService(service: IMetricsService): void {
    this.metricsServiceInstance = service;
  }

  static getMetricsReportsService(): IMetricsReportsService {
    if (!this.metricsReportsServiceInstance) {
      this.metricsReportsServiceInstance = new MetricsReportsService();
    }

    return this.metricsReportsServiceInstance;
  }

  static setMetricsReportsService(service: IMetricsReportsService): void {
    this.metricsReportsServiceInstance = service;
  }

  // Employee Services Methods
  static getEmployeeServicesService(): IEmployeeServicesService {
    if (!this.employeeServicesServiceInstance) {
      this.employeeServicesServiceInstance = new EmployeeServicesService({
        employeesService: this.getEmployeesService(),
      });
    }

    return this.employeeServicesServiceInstance;
  }

  static setEmployeeServicesService(service: IEmployeeServicesService): void {
    this.employeeServicesServiceInstance = service;
  }

  /** The notifications that announce a store's new online orders. */
  static getStoreNotificationsService(): IStoreNotificationsService {
    if (!this.storeNotificationsServiceInstance) {
      this.storeNotificationsServiceInstance = new StoreNotificationsService();
    }

    return this.storeNotificationsServiceInstance;
  }

  static setStoreNotificationsService(
    service: IStoreNotificationsService,
  ): void {
    this.storeNotificationsServiceInstance = service;
  }

  /**
   * Online orders the store ships itself (antiparasitics), from the Shopify
   * transfer API. `OnlineOrdersServiceMock` covers the shipping UI in tests
   * until the API can ship.
   */
  static getOnlineOrdersService(): IOnlineOrdersService {
    if (!this.onlineOrdersServiceInstance) {
      this.onlineOrdersServiceInstance = new OnlineOrdersService();
    }

    return this.onlineOrdersServiceInstance;
  }

  static setOnlineOrdersService(service: IOnlineOrdersService): void {
    this.onlineOrdersServiceInstance = service;
  }

  // Global Management Methods
  static clearAllInstances(): void {
    this.clientsServiceInstance = null;
    this.clientObservationsServiceInstance = null;
    this.clientFilesServiceInstance = null;
    this.appointmentsServiceInstance = null;
    this.attendeesServiceInstance = null;
    this.employeesServiceInstance = null;
    this.attendeeHistoryServiceInstance = null;
    this.attendeeFilesServiceInstance = null;
    this.refOptionsServiceInstance = null;
    this.refServicesServiceInstance = null;
    this.refStoresServiceInstance = null;
    this.refCommunicationTemplatesServiceInstance = null;
    this.filesServiceInstance = null;
    this.appointmentFilesServiceInstance = null;
    this.communicationServiceInstance = null;
    this.communicationManagementServiceInstance = null;
    this.employeeSlotsServiceInstance = null;
    this.ruleServiceDurationServiceInstance = null;
    this.saleItemsServiceInstance = null;
    this.stockServiceInstance = null;
    this.saleServiceInstance = null;
    this.healthboxServiceInstance = null;
    this.communicationsCenterServiceInstance = null;
    this.bannersServiceInstance = null;
    this.contentPagesServiceInstance = null;
    this.newsServiceInstance = null;
    this.metricsServiceInstance = null;
    this.metricsReportsServiceInstance = null;
    this.employeeServicesServiceInstance = null;
    this.onlineOrdersServiceInstance = null;
    this.storeNotificationsServiceInstance = null;
  }

  static clearClientsInstance(): void {
    this.clientsServiceInstance = null;
  }

  static clearAttendeesInstance(): void {
    this.attendeesServiceInstance = null;
  }

  static clearEmployeesInstance(): void {
    this.employeesServiceInstance = null;
  }

  static clearAttendeeHistoryInstance(): void {
    this.attendeeHistoryServiceInstance = null;
  }

  static clearRefOptionsInstance(): void {
    this.refOptionsServiceInstance = null;
  }

  static clearRefServicesInstance(): void {
    this.refServicesServiceInstance = null;
  }

  // Force specific service types
  static forceAllRealServices(): void {
    this.appointmentsServiceInstance = new AppointmentsService();
    this.clientsServiceInstance = new ClientsService();
    this.clientObservationsServiceInstance = new ClientObservationsService();
    this.clientFilesServiceInstance = new ClientFilesService();
    this.attendeesServiceInstance = new AttendeesService();
    this.employeesServiceInstance = new EmployeesService();
    this.attendeeHistoryServiceInstance = new AttendeeHistoryService();
    this.attendeeFilesServiceInstance = new AttendeeFilesService();
    this.refOptionsServiceInstance = new RefOptionsService();
    this.refServicesServiceInstance = new RefServicesService();
    this.ruleProfileServiceInstance = new RuleProfileService();
    this.refCommunicationTemplatesServiceInstance =
      new RefCommunicationTemplatesService();
    this.filesServiceInstance = new FilesService();
    this.appointmentFilesServiceInstance = new AppointmentFilesService();
    this.employeeSlotsServiceInstance = new EmployeeSlotsService();
    this.ruleServiceDurationServiceInstance = new RuleServiceDurationService();
    this.saleItemsServiceInstance = new SaleItemsService();
    this.healthboxServiceInstance = new HealthboxService();
  }

  static forceAllMockServices(): void {
    this.refOptionsServiceInstance = new RefOptionsServiceMock();
  }

  static forceClientsRealService(): void {
    this.clientsServiceInstance = new ClientsService();
  }

  static forceClientObservationsRealService(): void {
    this.clientObservationsServiceInstance = new ClientObservationsService();
  }

  static forceClientFilesRealService(): void {
    this.clientFilesServiceInstance = new ClientFilesService();
  }

  static forceAttendeesRealService(): void {
    this.attendeesServiceInstance = new AttendeesService();
  }

  static forceEmployeesRealService(): void {
    this.employeesServiceInstance = new EmployeesService();
  }

  static forceAttendeeHistoryRealService(): void {
    this.attendeeHistoryServiceInstance = new AttendeeHistoryService();
  }

  static forceRefOptionsRealService(): void {
    this.refOptionsServiceInstance = new RefOptionsService();
  }

  static forceRefOptionsMockService(): void {
    this.refOptionsServiceInstance = new RefOptionsServiceMock();
  }

  static forceRefServicesRealService(): void {
    this.refServicesServiceInstance = new RefServicesService();
  }

  static forceRuleProfileRealService(): void {
    this.ruleProfileServiceInstance = new RuleProfileService();
  }

  // Development helper - set environment-based defaults
  static setEnvironmentDefaults(
    environment: "development" | "production" | "test",
  ): void {
    const serviceType: ServiceType =
      environment === "development" || environment === "test" ? "mock" : "real";

    this.clearAllInstances();
    this.getAppointmentsService();
    this.getClientsService();
    this.getClientObservationsService();
    this.getClientFilesService();
    this.getAttendeesService();
    this.getEmployeesService();
    this.getAttendeeHistoryService();
    this.getAttendeeFilesService();
    this.getRefOptionsService(serviceType);
    this.getRefServicesService();
    this.getRuleProfileService();
    this.getRefCommunicationTemplatesService();
    this.getFilesService();
    this.getAppointmentFilesService();
    this.getEmployeeSlotsService();
    this.getRuleServiceDurationService();
    this.getSaleItemsService();
    this.getHealthboxService();
  }
}
